import {create} from 'zustand';

export interface AppliedCoupon {
  code: string;
  discount_type: 'percentage' | 'flat';
  discount_value: string;
}

export interface PendingCoupon {
  code: string;
  coupon: AppliedCoupon;
}

export type CouponSource =
  | 'tournament_registration'
  | 'subscription_renewal'
  | 'checkout'
  | string;

interface CouponState {
  // Local state
  couponCode: string;
  appliedCoupon: AppliedCoupon | null;
  pendingCoupon: PendingCoupon | null;
  couponError: string;
  isCouponValidating: boolean;

  // Configuration
  source: CouponSource | null;
  targetId: string | null; // e.g., tournament_id, plan_id

  // Actions
  setCouponCode: (code: string) => void;
  setAppliedCoupon: (coupon: AppliedCoupon | null) => void;
  setPendingCoupon: (coupon: PendingCoupon | null) => void;
  setCouponError: (error: string) => void;
  setIsCouponValidating: (loading: boolean) => void;
  setSource: (source: CouponSource) => void;
  setTargetId: (id: string | null) => void;

  // Computed helpers
  computeSavings: (basePrice: number | null) => number;
  computeFinalPrice: (basePrice: number | null) => number | null;
  reset: () => void;
}

const computeSavings = (
  coupon: AppliedCoupon | null,
  basePrice: number | null,
): number => {
  if (!basePrice || !coupon) {
    return 0;
  }
  const val = parseFloat(coupon.discount_value);
  return coupon.discount_type === 'percentage'
    ? Number(((basePrice * val) / 100).toFixed(2))
    : Math.min(val, basePrice);
};

const computeFinalPrice = (
  coupon: AppliedCoupon | null,
  basePrice: number,
): number => {
  if (!basePrice || !coupon) {
    return basePrice;
  }
  const val = parseFloat(coupon.discount_value);
  return coupon.discount_type === 'percentage'
    ? Math.max(0, basePrice - Number(((basePrice * val) / 100).toFixed(2)))
    : Math.max(0, basePrice - val);
};

export const useCouponStore = create<CouponState>((set, get) => ({
  couponCode: '',
  appliedCoupon: null,
  pendingCoupon: null,
  couponError: '',
  isCouponValidating: false,
  source: null,
  targetId: null,

  setCouponCode: code => set({couponCode: code}),
  setAppliedCoupon: coupon => set({appliedCoupon: coupon, couponError: ''}),
  setPendingCoupon: coupon => set({pendingCoupon: coupon}),
  setCouponError: error => set({couponError: error}),
  setIsCouponValidating: loading => set({isCouponValidating: loading}),
  setSource: source => set({source}),
  setTargetId: id => set({targetId: id}),

  computeSavings: (basePrice: number | null) => {
    const {appliedCoupon} = get();
    return computeSavings(appliedCoupon, basePrice);
  },

  computeFinalPrice: (basePrice: number | null) => {
    const {appliedCoupon} = get();
    return computeFinalPrice(appliedCoupon, basePrice);
  },

  reset: () =>
    set({
      couponCode: '',
      appliedCoupon: null,
      pendingCoupon: null,
      couponError: '',
      isCouponValidating: false,
      source: null,
      targetId: null,
    }),
}));

export {computeSavings, computeFinalPrice};
